import { useEffect, useState } from 'react';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import {
  Box,
  Card,
  CardHeader,
  Container,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { decryptString } from '../../../utils/url';
import { useAppDispatch } from '../../../store';
import Loader from '../../../components/Loader';
import { Users } from '../../../store/users/api/interfaces';
import { getUser, updateUserProfile } from '../../../store/users/api';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '../../../constants/countryCodes';
import { useFormik } from 'formik';
import userSchema from '../../../validation/userSchema';
import WalletTransactionTab from '../View/WalletTransactionsTab';
import useToggle from '../../../hooks/useToggle';
import Iconify from '../../../components/Iconify';

type Params = {
  id: string;
};


const Index = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<Params>();
  const [user, setUser] = useState<Users | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const {
    toggle: editCreditToggle,
    onOpen: onOpenEditCredit,
    onClose: onCloseEditCredit,
  } = useToggle();

  const decryptedId = id ? decryptString(id) : '';

  useEffect(() => {
    if (decryptedId) {
      dispatch(getUser({ id: decryptedId }))
        .unwrap()
        .then((response) => {
          setUser(response);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching user:', error);
          setIsLoading(false);
        });
    }
  }, [dispatch, decryptedId]);

  const formik = useFormik({
    initialValues: {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      mobileNumber: '',
      countryCallingCode: DEFAULT_COUNTRY_CODE,
      balance: 0,
      activeState: true,
      userRole: '', // Add userRole to initial values
    },
    validationSchema: userSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setShowError(false);
      
      try {
        const updateData = {
          id: values.id,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          mobileNumber: values.mobileNumber,
          countryCallingCode: values.countryCallingCode,
          activeState: values.activeState,
          userRole: user?.userRole, // Add userRole from the user object
        };

        await dispatch(updateUserProfile(updateData)).unwrap();
        setShowSuccess(true);
        
        // Navigate back to users list after successful update
        setTimeout(() => {
          navigate('/users');
        }, 1500);
      } catch (error: any) {
        // Handle different types of errors
        let errorMsg = 'An error occurred while updating the user';
        
        if (error?.message) {
          errorMsg = error.message;
        } else if (error?.data?.message) {
          errorMsg = error.data.message;
        } else if (error?.response?.data?.message) {
          errorMsg = error.response.data.message;
        }
        
        setErrorMessage(errorMsg);
        setShowError(true);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Update form values when user data is loaded
  useEffect(() => {
    if (user) {
      formik.setValues({
        id: user.id || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        mobileNumber: user.mobileNumber || '',
        countryCallingCode: user.countryCallingCode || DEFAULT_COUNTRY_CODE,
        balance: Number(user.balance) || 0,
        activeState: user.activeState || false,
        userRole: user.userRole || '', // Add userRole to form values
      });
    }
  }, [user]);

  const handleCloseSuccess = () => {
    setShowSuccess(false);
  };

  const handleCloseError = () => {
    setShowError(false);
  };


  if (isLoading) {
    return <Loader />;
  }

  if (!user) {
    return (
      <Page title="User Not Found">
        <Container maxWidth="xl">
          <Alert severity="error">User not found</Alert>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Edit Customer">
      <Container maxWidth="xl">
        <HeaderBreadcrumbs
          heading="Edit Customer"
          links={[
            { name: 'Dashboard', href: '/' },
            { name: 'Users', href: '/users' },
            { name: 'Edit Customer' },
          ]}
        />

        <Card>
          <CardHeader title="Customer Information" />
          <Box sx={{ p: 3 }}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    error={Boolean(formik.touched.firstName && formik.errors.firstName)}
                    helperText={formik.touched.firstName && formik.errors.firstName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    error={Boolean(formik.touched.lastName && formik.errors.lastName)}
                    helperText={formik.touched.lastName && formik.errors.lastName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Country Code</InputLabel>
                    <Select
                      name="countryCallingCode"
                      value={formik.values.countryCallingCode}
                      label="Country Code"
                      onChange={(e) => formik.setFieldValue('countryCallingCode', e.target.value)}
                    >
                      {COUNTRY_CODES.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.code} ({country.country})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    name="mobileNumber"
                    value={formik.values.mobileNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      formik.setFieldValue('mobileNumber', value);
                    }}
                    error={Boolean(formik.touched.mobileNumber && formik.errors.mobileNumber)}
                    helperText={formik.touched.mobileNumber && formik.errors.mobileNumber}
                    disabled={user?.userRole === 'admin'}
                    InputProps={user?.userRole === 'admin' ? {
                      sx: {
                        backgroundColor: '#f5f5f5',
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: '#666',
                        },
                      },
                    } : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={Boolean(formik.touched.email && formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    disabled={user?.userRole === 'admin'}
                    InputProps={user?.userRole === 'admin' ? {
                      sx: {
                        backgroundColor: '#f5f5f5',
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: '#666',
                        },
                      },
                    } : undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Balance"
                    name="balance"
                    value={`$${formik.values.balance || 0}`}
                    disabled
                    InputProps={{
                      sx: {
                        backgroundColor: '#f5f5f5',
                        color: '#EE3750',
                        fontWeight: 'bold',
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: '#EE3750',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="activeState"
                        checked={formik.values.activeState}
                        onChange={(e) => formik.setFieldValue('activeState', e.target.checked)}
                      />
                    }
                    label="Active Status"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  sx={{ backgroundColor: '#EE3750', '&:hover': { backgroundColor: '#EE3750' } }}
                >
                  Update Customer
                </LoadingButton>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/users')}
                  sx={{ borderColor: '#EE3750', color: '#EE3750' }}
                >
                  Cancel
                </Button>
              </Box>
            </form>
          </Box>
        </Card>

        <Card sx={{ my: 3 }}>
          <CardHeader
            title="Wallet Transactions"
            action={
              user ? (
                <Button
                  variant='contained'
                  startIcon={<Iconify icon={'eva:plus-fill'} />}
                  onClick={onOpenEditCredit}
                >
                  {'Edit Credit'}
                </Button>
              ) : null
            }
          />
          <Box sx={{ p: 1 }}>
            {user ? (
              <WalletTransactionTab
                user={user}
                toggle={editCreditToggle}
                onOpen={onOpenEditCredit}
                onClose={onCloseEditCredit}
                onBalanceUpdated={(balance) =>
                  setUser((prev) =>
                    prev ? { ...prev, balance: String(balance) } : prev
                  )
                }
              />
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Loading user data...
                </Typography>
              </Box>
            )}
          </Box>
        </Card>

        <Snackbar
          open={showSuccess}
          autoHideDuration={3000}
          onClose={handleCloseSuccess}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSuccess} severity="success">
            Customer updated successfully!
          </Alert>
        </Snackbar>

        <Snackbar
          open={showError}
          autoHideDuration={5000}
          onClose={handleCloseError}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseError} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Page>
  );
};

export default Index;
